import { Box } from "@mui/material";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetServiceApiValuationReportStatusQuery } from "@/api/generatedApi";
import {
  MarketAnalysisPanel,
  RealEstateDataPanel,
  RefineRealEstate,
  ValuationReportConfigFormDataProvider,
} from "@/components";
import { DataGroupType } from "@/components/refineRealEstate/dataGroupsConstants";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { PropertyInformation } from "@/types/refineRealEstate";

import { updatePropertyDetails } from "./valuationReportSlice";

const styles = {
  pageGrid: {
    height: "fit-content",
    display: "grid",
    gridTemplateRows: "auto 1fr",
    gap: "30px",
    padding: "76px 10px 10px 10px",
  },
  valuationDetailsContainer: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "1fr 1fr",
    },
    gap: "30px",
  },
};

interface ValuationReportContainerProps {
  propertyDataIsLoading?: boolean;
  marketPriceIsLoading?: boolean;
  avmValuateIsLoading?: boolean;
  refetchStatus: ReturnType<typeof useGetServiceApiValuationReportStatusQuery>["refetch"];
}

export const ValuationReportContainer = ({
  marketPriceIsLoading,
  avmValuateIsLoading,
  propertyDataIsLoading,
}: ValuationReportContainerProps): JSX.Element => {
  const { t } = useTranslation("ValuationReport");
  const [, setIsPropertyDataValid] = useState(false);

  const dispatch = useAppDispatch();

  const [dataGroupToRefine, setDataGroupToRefine] = useState<DataGroupType>();
  const address = useAppSelector((state) => state.valuationReport.address);
  const propertyInformation = useAppSelector((state) => state.valuationReport.propertyInformation);
  const images = propertyInformation?.propertyImages.images ?? null;
  const marketPrice = useAppSelector((state) => state.valuationReport.prosaWeb);
  const priceRange = useAppSelector((state) => state.valuationReport.avm);
  const demandScore = useAppSelector((state) => state.valuationReport.demandScore);
  const selectedEquipmentPriceRange = useAppSelector((state) => state.valuationReport.selectedEquipmentPriceRange);
  const contact = useAppSelector((state) => state.valuationReport.contact);

  return (
    <Box sx={styles.pageGrid}>
      {dataGroupToRefine && propertyInformation && (
        <Box sx={{ gridRow: "1 / 3" }}>
          <RefineRealEstate
            address={address}
            propertyDetails={propertyInformation}
            setRefineData={setDataGroupToRefine}
            initiallySelectedTab={dataGroupToRefine}
            setIsDataValid={setIsPropertyDataValid}
            updatePropertyDetails={(formData: PropertyInformation) => dispatch(updatePropertyDetails(formData))}
          />
        </Box>
      )}
      <ValuationReportConfigFormDataProvider
        prosaWebPriceResult={marketPrice}
        avmPriceResult={priceRange}
        images={images}
        contact={contact}
      >
        {!dataGroupToRefine && (
          <>
            <Box sx={styles.valuationDetailsContainer}>
              <RealEstateDataPanel
                panelDescription={t("propertyData.propertyDataDescription")}
                address={address}
                propertyInformation={propertyInformation}
                images={images}
                setRefineData={setDataGroupToRefine}
                propertyDataIsLoading={propertyDataIsLoading}
                valuationIsLoading={marketPriceIsLoading || avmValuateIsLoading}
              />
              <MarketAnalysisPanel
                propertyInformation={propertyInformation}
                marketPrice={marketPrice}
                priceRange={priceRange}
                demandScore={demandScore}
                selectedEquipmentPriceRange={selectedEquipmentPriceRange}
                prosaWebValuateIsLoading={marketPriceIsLoading}
                avmValuateIsLoading={avmValuateIsLoading}
              />
            </Box>
          </>
        )}
      </ValuationReportConfigFormDataProvider>
      <Box sx={{ height: "100%" }}></Box>
    </Box>
  );
};
